// biome-ignore-all lint/a11y: TODO - address a11y issues later
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { WebsitePageMetrics } from "@/hooks/api/use-website-page-metrics";
import type { WebsitePage } from "@/hooks/api/use-website-page-search";
import { DEFAULT_PRIMARY_COLUMN_WIDTHS } from "@/lib/column-defaults";
import { formatDate } from "@/lib/date-formatting";
import { createCustomFieldColumns } from "@/pages/accounts/account-list/custom-field-column-utils";
import {
  METRIC_CONFIG,
  METRIC_DATA_TYPES,
} from "@/pages/webpages/webpage-explorer/webpage-metrics-config";
import type { CustomFieldDefinition } from "@/types/custom-fields";
import type { FormulaTableColumn } from "@/types/formula-column";
import type { UnifiedTableColumn } from "@/types/table-column";

/** Webpage with formula columns */
export interface WebpageWithFormulaColumns
  extends Omit<WebsitePage, keyof Record<string, unknown>> {
  formulaColumns?: Record<string, unknown>;
  [key: string]: string | number | Record<string, unknown> | undefined;
}

/** Webpage column with metric category information */
export interface WebpageColumn extends UnifiedTableColumn<WebsitePage> {
  category?: string;
  categoryStyle?: string;
}

export const getWebpageColumns = (
  filterOptions?: { page_type?: string[] },
  selectedMetrics?: Array<keyof WebsitePageMetrics>,
  formulaColumns?: FormulaTableColumn[],
  customFields?: CustomFieldDefinition[],
  onMetricClick?: (
    webpageUrl: string,
    metric: keyof WebsitePageMetrics,
  ) => void,
): WebpageColumn[] => {
  // Base columns (existing columns)
  const baseColumns = [
    {
      key: "url",
      title: "URL",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: DEFAULT_PRIMARY_COLUMN_WIDTHS.XLARGE,
      required: true, // This is the primary column that cannot be hidden
      render: (webpage: WebsitePage) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2 w-full">
              <Link
                to={`/webpages/${encodeURIComponent(webpage.url)}/explorer`}
                className="text-foreground p-4 rounded hover:bg-accent transition-colors block flex-1"
                title={webpage.url}
              >
                {webpage.url}
              </Link>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>View in Webpage Explorer</p>
          </TooltipContent>
        </Tooltip>
      ),
    },
    {
      key: "page_type",
      title: "Page Type",
      sortable: true,
      filterable: true,
      filterType: "select",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 150, // Fixed
      filterOptions:
        filterOptions?.page_type?.map((type) => ({
          id: type,
          label: type,
        })) || [],
      render: (webpage: WebsitePage) => (
        <Badge variant="secondary" className="text-xs">
          {webpage.page_type || "-"}
        </Badge>
      ),
    },
  ] as UnifiedTableColumn<WebsitePage>[];

  // Add metric columns if selectedMetrics is provided
  if (selectedMetrics && selectedMetrics.length > 0) {
    // Filter out any metric keys that do not have a corresponding config to avoid runtime errors
    const validMetrics = selectedMetrics.filter(
      (metricKey) =>
        METRIC_CONFIG[metricKey as keyof WebsitePageMetrics] !== undefined,
    );

    // Optional: Warn in the console if any selected metrics were ignored due to missing config
    if (validMetrics.length < selectedMetrics.length) {
      console.warn(
        "Ignored metrics without configuration:",
        selectedMetrics.filter(
          (m) => METRIC_CONFIG[m as keyof WebsitePageMetrics] === undefined,
        ),
      );
    }

    const metricColumns = validMetrics.map((metricKey) => {
      const metricConfig = METRIC_CONFIG[metricKey];
      const dataType = METRIC_DATA_TYPES[metricKey];

      // Map data types to filter types
      const getFilterType = (dataType: string): string => {
        switch (dataType) {
          case "integer":
          case "float":
          case "currency":
            return "number";
          case "date":
            return "date";
          default:
            return "text";
        }
      };

      return {
        key: `metric_${metricKey}`,
        title: metricConfig.title,
        tooltip: metricConfig.tooltip,
        columnType: "metric",
        sortable: true,
        filterable: true,
        filterType: getFilterType(dataType),
        nullSort: "last",
        defaultSortDirection: "desc",
        render: (webpage: WebsitePage) => {
          // Metrics are returned at the top level of the webpage object from the API
          // The WebsitePage interface includes the metric fields directly
          const metricValue = (webpage as unknown as Record<string, unknown>)[
            metricKey
          ];
          if (metricValue === undefined || metricValue === null) {
            return <span className="text-muted-foreground">-</span>;
          }

          // Use data type from METRIC_DATA_TYPES for rendering
          const dataType = METRIC_DATA_TYPES[metricKey];

          const formatValue = (
            value: number | string,
            type: string,
          ): string => {
            switch (type) {
              case "currency":
                return (value as number).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                  maximumFractionDigits: 0,
                });
              case "float":
                return (value as number).toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                });
              case "date":
                return formatDate(value as string);
              default:
                return (value as number).toLocaleString("en-US");
            }
          };

          const formattedContent = (
            <span className="font-medium">
              {formatValue(metricValue as string | number, dataType)}
            </span>
          );

          // Check if this metric supports drill-down
          const supportsClickableDrilldown =
            "drilldownColumns" in metricConfig &&
            metricConfig.drilldownColumns &&
            Array.isArray(metricConfig.drilldownColumns) &&
            metricConfig.drilldownColumns.length > 0;

          const clickableContent =
            onMetricClick && supportsClickableDrilldown ? (
              <button
                className="w-full h-full text-left p-0 hover:underline decoration-2 underline-offset-4 transition-all cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onMetricClick(webpage.url, metricKey);
                }}
              >
                {formattedContent}
              </button>
            ) : (
              formattedContent
            );

          return clickableContent;
        },
        exportValue: (webpage: WebsitePage | WebpageWithFormulaColumns) => {
          // Metrics are at the top level of the webpage object
          const metricValue = (webpage as unknown as Record<string, unknown>)[
            metricKey
          ];
          if (metricValue === undefined || metricValue === null) {
            return "";
          }

          return String(metricValue);
        },
        category: metricConfig.category,
        categoryStyle: CATEGORY_STYLES[metricConfig.category]?.color,
      } as UnifiedTableColumn<WebsitePage>;
    });

    const columnsWithMetrics = [...baseColumns, ...metricColumns];

    // Combine formula columns and custom field columns
    const additionalColumns = [
      ...(formulaColumns || []),
      ...(customFields && customFields.length > 0
        ? createCustomFieldColumns(customFields)
        : []),
    ];

    return [...columnsWithMetrics, ...additionalColumns] as WebpageColumn[];
  }

  // When no metrics are selected, include formula columns and custom field columns
  const additionalColumns = [
    ...(formulaColumns || []),
    ...(customFields && customFields.length > 0
      ? createCustomFieldColumns(customFields)
      : []),
  ];

  return [...baseColumns, ...additionalColumns] as WebpageColumn[];
};

// Helper: convert UnifiedTableColumn list to TanStack ColumnDef list for useReactTable
// Note: This does a minimal mapping: header, cell renderer, size, id/accessorKey.
// Resize, sorting & filtering will be handled in webpages-data-table.tsx via external logic.
export const getWebpageColumnDefs = (
  filterOptions?: { page_type?: string[] },
  selectedMetrics?: Array<keyof WebsitePageMetrics>,
  formulaColumns?: FormulaTableColumn[],
  customFields?: CustomFieldDefinition[],
  onMetricClick?: (
    webpageUrl: string,
    metric: keyof WebsitePageMetrics,
  ) => void,
): ColumnDef<WebsitePage>[] => {
  const unifiedColumns = getWebpageColumns(
    filterOptions,
    selectedMetrics,
    formulaColumns,
    customFields,
    onMetricClick,
  );

  // Exclude columns that are for export only
  return unifiedColumns
    .filter((col) => !col.exportOnly)
    .map((col): ColumnDef<WebsitePage> => {
      const colId = col.key.toString();

      // Basic cell renderer: use provided render or raw value
      const cellRenderer = col.render
        ? (info: { row: { original: WebsitePage } }) =>
            typeof col.render === "function"
              ? col.render(info.row.original)
              : "-"
        : (info: { getValue: () => unknown }) => {
            const val = info.getValue();
            return val === null || val === undefined || val === ""
              ? "-"
              : (val as React.ReactNode);
          };

      // Use column size
      const size = col.size;

      return {
        id: colId,
        accessorKey: typeof col.key === "string" ? col.key : undefined,
        header: () => col.title,
        cell: cellRenderer,
        enableSorting: !!col.sortable,
        size,
        meta: {
          title: col.title, // Store the column title in meta for accessibility
          categoryStyle: col.categoryStyle,
          sortable: col.sortable,
          filterable: col.filterable,
          filterType: col.filterType,
          filterOptions: col.filterOptions,
          defaultSortDirection: col.defaultSortDirection,
          nullSort: col.nullSort,
          isCustomField: col.isCustomField,
          customFieldDataType: col.customFieldDataType,
          dataType: col.dataType, // Include simple data type string
          required: col.required, // Pass through the required flag
        },
      } as ColumnDef<WebsitePage>;
    });
};
